import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { RefreshCw, UserCog, ShieldAlert, MapPin, Route as RouteIcon, AlertTriangle } from "lucide-react";

const STATUS_COLOR = {
  "seated":     "hsl(var(--success))",
  "en-route":   "hsl(var(--warning))",
  "in-meeting": "hsl(var(--primary))",
  "off-site":   "hsl(var(--muted-foreground))",
};

const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return `${Math.max(1, Math.floor(d))}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  return `${Math.floor(d / 3600)}h ago`;
};

const Initials = ({ name, color }) => {
  const parts = name.replace(/[^A-Za-z. ]/g, "").split(" ").filter(Boolean);
  const inits = (parts.slice(0, 2).map((p) => p[0]).join("") || "?").toUpperCase();
  return (
    <div className="w-10 h-10 flex items-center justify-center font-display font-black text-sm tracking-tight"
         style={{ background: `${color || "#00E5FF"}22`, color: color || "#00E5FF", border: `1.5px solid ${color || "#00E5FF"}` }}>
      {inits}
    </div>
  );
};

const RouteBar = ({ route = [], currentZone }) => {
  if (!route.length) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {route.map((z, i) => {
        const active = z === currentZone;
        const passed = route.indexOf(currentZone) > i;
        return (
          <React.Fragment key={`${z}-${i}`}>
            <span className={["px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border",
              active ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)]" :
              passed ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" :
                       "border-border text-muted-foreground"].join(" ")}>
              {z.replace(/-/g, " ")}
            </span>
            {i < route.length - 1 && <span className="text-muted-foreground text-[10px]">→</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default function VIP() {
  const [vips, setVips] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const d = await http.get("/vip/profiles").then((r) => r.data);
      setVips(d.vips ?? []);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const alertsCount = vips.reduce((acc, v) => acc + (v.proximity_alerts?.length || 0), 0);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black uppercase tracking-tight text-sm leading-none">VIP Protection</h1>
          <span className="micro-label hidden sm:inline">Profiles · Routes · Proximity</span>
        </div>
        <div className="flex items-center gap-2">
          {alertsCount > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] flex items-center gap-1" data-testid="vip-proximity-badge">
              <AlertTriangle className="w-3 h-3" /> {alertsCount} proximity
            </span>
          )}
          <button onClick={load} className="btn-tac !px-2 !py-1.5" data-testid="vip-refresh-btn" aria-label="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 min-h-0">
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))" }} data-testid="vip-grid">
          {vips.map((v) => (
            <div key={v.id} className="panel p-3 flex flex-col gap-2" data-testid={`vip-card-${v.id}`}>
              <div className="flex items-start gap-3">
                <Initials name={v.name} color={v.color} />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-black uppercase tracking-tight text-sm leading-tight truncate">{v.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 truncate">{v.role}</div>
                </div>
                <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border whitespace-nowrap"
                  style={{ color: STATUS_COLOR[v.status] || "hsl(var(--primary))", borderColor: STATUS_COLOR[v.status] || "hsl(var(--primary))" }}>
                  {v.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                <div className="flex items-center gap-1.5 min-w-0">
                  <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">@</span>
                  <span className="truncate">{v.current_zone}</span>
                </div>
                <div className="flex items-center gap-1.5 min-w-0">
                  <ShieldAlert className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="truncate">{v.escort}</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="micro-label flex items-center gap-1"><RouteIcon className="w-3 h-3" /> Route</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{Math.round((v.route_progress || 0) * 100)}% complete</span>
                </div>
                <RouteBar route={v.route} currentZone={v.current_zone} />
                <div className="mt-1.5 h-1 bg-border w-full">
                  <div className="h-full bg-[hsl(var(--primary))] transition-all" style={{ width: `${(v.route_progress || 0) * 100}%` }} />
                </div>
              </div>

              {v.proximity_alerts?.length > 0 && (
                <div className="panel-elevated p-2 border-l-2 !border-l-[hsl(var(--destructive))]">
                  <div className="micro-label flex items-center gap-1 mb-1 sev-critical">
                    <AlertTriangle className="w-3 h-3" /> Proximity Alerts · {v.proximity_alerts.length}
                  </div>
                  <ul className="space-y-0.5">
                    {v.proximity_alerts.map((a) => (
                      <li key={a.event_id} className="font-mono text-[10px] flex items-center gap-2">
                        <span className="uppercase tracking-widest font-bold" style={{ color: a.severity === "critical" ? "hsl(var(--destructive))" : "hsl(var(--warning))" }}>{a.severity}</span>
                        <span className="truncate">{a.title}</span>
                        <span className="text-muted-foreground ml-auto shrink-0">{fmtRel(a.ts)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
          {vips.length === 0 && (
            <div className="col-span-full p-6 text-center text-xs text-muted-foreground"><UserCog className="w-6 h-6 mx-auto mb-2 opacity-50" />Loading VIP roster…</div>
          )}
        </div>
      </div>
    </div>
  );
}
